import React, { useEffect, useMemo, useState } from "react";
import "../../styles/theme-dark.css";
import "../Clientes.css";
import { Link, useLocation } from "react-router-dom";
import axios from "../../api/axios";

const formatEuro = (number) =>
  new Intl.NumberFormat("es-ES", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Number(number) || 0) + " €";

const EstadoBadge = ({ estado }) => {
  const config = {
    pendiente: { color: "#fca5a5", bg: "rgba(248,113,113,0.15)", text: "PENDIENTE" },
    parcialmente_pagada: {
      color: "#fbbf24",
      bg: "rgba(251,191,36,0.15)",
      text: "PARCIAL",
    },
    pagada: { color: "#6ee7b7", bg: "rgba(16,185,129,0.20)", text: "PAGADA" },
  };

  const { color, bg, text } = config[estado] || config.pendiente;

  return (
    <span
      style={{
        display: "inline-block",
        padding: "4px 8px",
        borderRadius: "999px",
        fontSize: "11px",
        fontWeight: 600,
        backgroundColor: bg,
        color,
        border: `1px solid ${color}40`,
        textTransform: "uppercase",
        letterSpacing: "0.5px",
      }}
    >
      {text}
    </span>
  );
};

// Diferencia en días entre hoy y una fecha
const diasDiferencia = (fechaStr) => {
  if (!fechaStr) return null;
  const hoy = new Date();
  const fecha = new Date(fechaStr);
  const diff = Math.floor((fecha - hoy) / (1000 * 60 * 60 * 24));
  return diff;
};

export default function ComprasList() {
  const [compras, setCompras] = useState([]);
  const [query, setQuery] = useState("");

  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const activeFilter = params.get("filter");

  const load = async () => {
    const res = await axios.get("/compras");
    setCompras(res.data || []);
  };

  useEffect(() => {
    load();
  }, []);

  const filtered = useMemo(() => {
    let list = [...compras];

    const hoy = new Date();
    const mesActual = hoy.getMonth();
    const añoActual = hoy.getFullYear();

    if (activeFilter) {
      list = list.filter((c) => {
        const d = diasDiferencia(c.fechaVencimiento);

        if (activeFilter === "vencidas") {
          return d < 0 && c.estado !== "pagada";
        }

        if (activeFilter === "proximas") {
          return d !== null && d >= 0 && d <= 10 && c.estado !== "pagada";
        }

        if (activeFilter === "pagadasMes") {
          if (c.estado !== "pagada") return false;

          const pagos = c.pagos || [];

          return pagos.some((p) => {
            if (!p.fecha) return false;
            const fp = new Date(p.fecha);
            return fp.getMonth() === mesActual && fp.getFullYear() === añoActual;
          });
        }

        return true;
      });
    }

    if (query.trim()) {
      const q = query.toLowerCase();
      list = list.filter((c) => {
        return (
          (c.proveedorNombre || "").toLowerCase().includes(q) ||
          (c.numeroFactura || "").toLowerCase().includes(q) ||
          (c.tipo || "").toLowerCase().includes(q)
        );
      });
    }

    return list;
  }, [query, compras, activeFilter]);

  // Resumen financiero
  const totalCompras = filtered.reduce(
    (acc, c) => acc + (Number(c.total) || 0),
    0
  );

  const totalPagado = filtered.reduce((acc, c) => {
    const pagadoCompra = (c.pagos || []).reduce(
      (p, pago) => p + (Number(pago.monto ?? pago.importe) || 0),
      0
    );
    return acc + pagadoCompra;
  }, 0);

  const totalPendiente = totalCompras - totalPagado;

  return (
    <div className="page-dark">
      {/* HEADER DARK GLASS */}
      <div
        className="glass-box"
        style={{
          marginBottom: 18,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 20,
        }}
      >
        <div>
          <h1 style={{ margin: 0, fontSize: 26, fontWeight: 700 }}>
            Compras
          </h1>
          <p style={{ marginTop: 4, opacity: 0.85 }}>
            Facturas recibidas, vencimientos y pagos a proveedores.
          </p>
        </div>

        <button
          type="button"
          onClick={() => (window.location.href = "/")}
          className="glass-btn"
          style={{ padding: "10px 20px", borderRadius: 12 }}
        >
          ← Menú principal
        </button>
      </div>

      {/* PANEL RESUMEN DARK */}
      <div
        className="glass-box"
        style={{
          marginBottom: 20,
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
          gap: 18,
          alignItems: "stretch",
        }}
      >
        <div>
          <span style={{ fontSize: 13, opacity: 0.7 }}>Total Compras</span>
          <div style={{ fontSize: 26, fontWeight: 700, marginTop: 4 }}>
            {formatEuro(totalCompras)}
          </div>
        </div>

        <div>
          <span style={{ fontSize: 13, opacity: 0.7 }}>Total Pagado</span>
          <div
            style={{
              fontSize: 26,
              fontWeight: 700,
              marginTop: 4,
              color: "#22c55e",
            }}
          >
            {formatEuro(totalPagado)}
          </div>
        </div>

        <div>
          <span style={{ fontSize: 13, opacity: 0.7 }}>Total Pendiente</span>
          <div
            style={{
              fontSize: 26,
              fontWeight: 700,
              marginTop: 4,
              color: totalPendiente > 0 ? "#f97373" : "#22c55e",
            }}
          >
            {formatEuro(totalPendiente)}
          </div>
        </div>
      </div>

      {/* BUSCADOR + NUEVA COMPRA */}
      <div
        style={{
          marginBottom: 10,
          display: "flex",
          alignItems: "center",
          gap: 12,
        }}
      >
        <input
          className="glass-input"
          type="search"
          placeholder="Buscar proveedor, nº factura, tipo…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          style={{ maxWidth: 420 }}
        />

        <div style={{ flex: 1 }} />

        <Link to="/compras/nueva">
          <button className="glass-btn" style={{ padding: "9px 16px" }}>
            + Nueva compra
          </button>
        </Link>
      </div>

      {/* TABLA GLASS DARK */}
      <div className="glass-box">
        <table className="glass-table compact-table">
          <thead>
            <tr>
              <th>Proveedor</th>
              <th>Nº Factura</th>
              <th>Fecha</th>
              <th>Vencimiento</th>
              <th>Estado</th>
              <th>Base</th>
              <th>IVA</th>
              <th>Total</th>
              <th>Pagado</th>
              <th>Pendiente</th>
              <th style={{ width: 220, textAlign: "center" }}>Acciones</th>
            </tr>
          </thead>

          <tbody>
            {filtered.map((c) => {
              const pagado = (c.pagos || []).reduce(
                (acc, p) =>
                  acc + (Number(p.monto ?? p.importe) || 0),
                0
              );
              const pendiente = (Number(c.total) || 0) - pagado;
              const d = diasDiferencia(c.fechaVencimiento);

              return (
                <tr key={c.id}>
                  <td
                    style={{
                      fontSize: 14,
                      fontWeight: 500,
                      maxWidth: 260,
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {c.proveedorNombre}
                  </td>

                  <td>
                    <strong style={{ fontSize: 12 }}>
                      {c.numeroFactura}
                    </strong>
                  </td>

                  <td style={{ fontSize: 12 }}>
                    {c.fecha ? new Date(c.fecha).toLocaleDateString() : "—"}
                  </td>

                  {/* VENCIMIENTO */}
                  <td style={{ fontSize: 12, fontWeight: 600 }}>
                    {(() => {
                      if (!c.fechaVencimiento) {
                        return (
                          <span style={{ color: "#9ca3af" }}>— Sin fecha</span>
                        );
                      }

                      if (c.estado === "pagada") {
                        return <span style={{ color: "#22c55e" }}>Pagado ✔</span>;
                      }

                      if (d < 0) {
                        return (
                          <span style={{ color: "#f97373" }}>
                            Vencido hace {Math.abs(d)} días
                          </span>
                        );
                      }

                      if (d >= 0 && d <= 10) {
                        return (
                          <span style={{ color: "#fbbf24" }}>
                            Vence en {d} días
                          </span>
                        );
                      }

                      return (
                        <span style={{ color: "#93c5fd" }}>
                          {new Date(c.fechaVencimiento).toLocaleDateString()}
                        </span>
                      );
                    })()}
                  </td>

                  <td style={{ textAlign: "center" }}>
                    <EstadoBadge estado={c.estado} />
                  </td>

                  <td style={{ fontSize: 12, textAlign: "right" }}>
                    {formatEuro(c.subtotal)}
                  </td>
                  <td style={{ fontSize: 12, textAlign: "right" }}>
                    {formatEuro(c.ivaTotal)}
                  </td>
                  <td
                    style={{
                      fontSize: 12,
                      textAlign: "right",
                      fontWeight: 600,
                    }}
                  >
                    {formatEuro(c.total)}
                  </td>
                  <td style={{ fontSize: 12, textAlign: "right" }}>
                    {formatEuro(pagado)}
                  </td>
                  <td style={{ fontSize: 12, textAlign: "right" }}>
                    <span
                      style={{
                        color: pendiente > 0 ? "#f97373" : "#22c55e",
                        fontWeight: 600,
                      }}
                    >
                      {formatEuro(pendiente)}
                    </span>
                  </td>

                  <td style={{ textAlign: "center" }}>
                    <div className="action-buttons-compact">
                      <Link to={`/compras/editar/${c.id}`}>
                        <button className="icon-btn edit-btn">✏️</button>
                      </Link>

                      <Link to={`/compras/pagos/${c.id}`}>
                        <button className="icon-btn paid-btn">💰</button>
                      </Link>

                      <button
                        className="icon-btn delete-btn"
                        onClick={async () => {
                          if (!window.confirm("¿Eliminar compra?")) return;
                          await axios.delete(`/compras/${c.id}`);
                          load();
                        }}
                      >
                        🗑️
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}

            {filtered.length === 0 && (
              <tr>
                <td colSpan="11" style={{ textAlign: "center", opacity: 0.6 }}>
                  No hay resultados
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
